import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { OneLineInput } from '../⚛️atoms/OneLineInput';
import { useDebounce } from 'react-use';
import { MultiLineInput } from '../⚛️atoms/MultiLineInput';
import { TiptapEditor } from '../⚛️atoms/TiptapEditor';
import { Checkbox } from '../⚛️atoms/Checkbox';
import { getDataAPI, patchDataAPI } from '../../Helpers/FetchAPI';
import toast from 'react-hot-toast';
import { ONE_TOAST_ID } from '../../constant';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { MarkdownWrapper } from './MarkdownWrapper';
import { getTranslation, updateTranslation } from '../../utils/translationHelper';
import useLockedTranslate from '../../Hooks/useLockedTranslate';
import { TranslateButton } from '../atoms/translateButton';
import { cloneWorkbookV2Value } from './editorUtils';
import {
  buildWorkbookItemSavePayload,
  getItemByTemplateApiPath,
  getTemplateItemId,
  WORKBOOK_V2_ANSWER_SAVE_DEBOUNCE_MS
} from '../../utils/workbookV2';

export const WorkbookV2TextInput = forwardRef(
  ({ item, updateItem, editing, editor, oldAnswer, editingLanguage = null, workbook, creditId, lessonId }, ref) => {
    const [answer, setAnswer] = useState(item.answer ?? '');
    const [isCorrect, setIsCorrect] = useState(true);
    const oldQuestionInitializedRef = useRef(false);
    const { t, lang } = useLockedTranslate(workbook);

    const updateEditorItem = (updates) => {
      const nextItem = cloneWorkbookV2Value(item);
      nextItem.text = updates.text ?? nextItem.text;
      nextItem.page_number = updates.page_number ?? nextItem.page_number;
      nextItem.old_question_id = updates.old_question_id ?? nextItem.old_question_id;
      nextItem.translations = updates.translations ?? nextItem.translations;
      nextItem.options = {
        ...(nextItem.options ?? {}),
        ...(updates.options ?? {})
      };
      updateItem(nextItem);
    };

    useEffect(() => {
      if (oldAnswer?.length && !answer) {
        setAnswer(oldAnswer.join(' '));
      }
    }, [answer, oldAnswer]);

    useDebounce(
      () => {
        if (!editing) {
          return;
        }

        if (!oldQuestionInitializedRef.current) {
          oldQuestionInitializedRef.current = true;
          return;
        }

        if (item.old_question_id) {
          toast.promise(
            getDataAPI(`/workbookItems/question/${item.old_question_id}`).then((response) => {
              const question = response.data.questions.find((currentQuestion) => currentQuestion._id === item.old_question_id);
              if (['textarea', 'text'].includes(question.type)) {
                updateEditorItem({ text: question.title });
              }
              return question;
            }),
            {
              loading: t('Getting Old Question Data'),
              success: (question) => `${t('The old Question was a')} ${question.type}`,
              error: t('Error getting old question data')
            },
            { id: 'old-question' }
          );
        }
      },
      250,
      [editing, item.old_question_id]
    );

    useDebounce(
      () => {
        if (editor) {
          return;
        }
        toast.promise(
          patchDataAPI(
            getItemByTemplateApiPath(creditId, getTemplateItemId(item)),
            buildWorkbookItemSavePayload({ answer, lessonId })
          ),
          {
            loading: t('Saving...'),
            success: t('Saved'),
            error: t('Error saving')
          },
          { id: ONE_TOAST_ID }
        );
      },
      WORKBOOK_V2_ANSWER_SAVE_DEBOUNCE_MS,
      [answer, creditId, editor, item, lessonId, t]
    );

    useImperativeHandle(ref, () => ({
      checkAnswers() {
        if (!item.options?.required) {
          return true;
        }
        setIsCorrect(!!answer);
        return !!answer;
      }
    }));

    return (
      <div className={`flex w-full items-center gap-2 ${editing && 'border-pdteal rounded-lg border-2 px-8 py-12'}`}>
        {!editing && (
          <div className="relative flex w-full flex-col gap-2">
            <div
              className={`bg-pdred/80 absolute bottom-[4%] -left-5 w-1.5 ${isCorrect ? 'h-[10%] opacity-0' : 'h-[94%] opacity-100'} transition-all duration-300 ease-out`}
            >
              <XMarkIcon className="bg-pdred/80 absolute top-0 -left-7 size-7 rounded-l-md pl-0.5 text-white" />
            </div>
            <span>
              <MarkdownWrapper>{lang === 'en' ? item.text : getTranslation(item.translations, lang, 'text', item.text)}</MarkdownWrapper>
            </span>
            {(item.options?.number_of_lines ?? 10) > 1 ? (
              <MultiLineInput
                value={answer}
                onChange={setAnswer}
                placeholder={t('Type your answer here...')}
                lines={item.options?.number_of_lines ?? 10}
                required={item.options?.required}
              />
            ) : (
              <OneLineInput
                value={answer}
                onChange={setAnswer}
                placeholder={t('Type your answer here...')}
                required={item.options?.required}
              />
            )}
          </div>
        )}
        {editing && (
          <div className="grid w-full grid-cols-2 gap-8">
            <div className="relative">
              <p className="mb-2 text-xl/6 font-medium text-gray-900">Prompt</p>
              <TiptapEditor content={item.text} onChange={(value) => updateEditorItem({ text: value })} />
              {editingLanguage && (
                <TranslateButton
                  text={item.text}
                  language={editingLanguage}
                  onResponse={(value) =>
                    updateEditorItem({
                      translations: updateTranslation(item.translations ?? {}, editingLanguage, 'text', value)
                    })
                  }
                />
              )}
            </div>
            {editingLanguage && (
              <div>
                <p className="mb-2 text-xl/6 font-medium text-gray-900">Prompt ({editingLanguage})</p>
                <TiptapEditor
                  content={getTranslation(item.translations, editingLanguage, 'text')}
                  onChange={(value) =>
                    updateEditorItem({
                      translations: updateTranslation(item.translations ?? {}, editingLanguage, 'text', value)
                    })
                  }
                />
              </div>
            )}
            <div className="col-start-1 w-2/5">
              <OneLineInput
                value={item.page_number}
                label="Page Number"
                onChange={(value) => updateEditorItem({ page_number: value })}
              />
            </div>
            <OneLineInput
              value={item.old_question_id}
              label="Old Question ID"
              onChange={(value) => updateEditorItem({ old_question_id: value })}
            />
            <div className="w-1/5">
              <Checkbox
                label="Required"
                checked={item.options?.required ?? false}
                onChange={(value) => updateEditorItem({ options: { required: value } })}
              />
            </div>
            <div className="w-2/5">
              <OneLineInput
                value={item.options?.number_of_lines ?? 10}
                label="Number of Lines"
                onChange={(value) => updateEditorItem({ options: { number_of_lines: value } })}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
);
